import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useLocation } from 'react-router-dom';
import useEditorStore from '../../stores/useEditorStore';
import { useWorkflowState, STEP_CONFIGS } from '../../stores/useWorkflowStore';
import { useShallow } from 'zustand/shallow';
import { SCAN_PROCESS_STEPS, MAIN_PROCESS_STEPS, isFinalizationPath, isPreviewStepPath } from '../../constants/flowConfig';
import { getToolHelpPanel } from '../../constants/toolHelpContent';
import { TOOL_NAMES } from '../../constants/tools';

import changeCameraDirection from '../../lib/CameraTransformValues';
import ViewportGizmo from './ViewportGizmo';
import { RotateCcw, RotateCw, Maximize2 } from 'lucide-react';

// Import tool components
import RectificationTool from '../../services/operations/rectification/RectificationTool';
import MeasurementTool from '../../services/operations/MeasurementTool';

const simpleEyebrow =
  'inline-flex items-center rounded-full border border-violet-200/90 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-violet-800';
const simpleTitle =
  'mt-2 text-[15px] font-semibold leading-tight text-slate-900 text-pretty';
const simpleBody = 'mt-1 text-xs leading-relaxed text-slate-600';
const simpleHint =
  'mt-2 border-t border-slate-200 pt-2 text-[11px] leading-relaxed text-slate-500';
const helpPopoverSection =
  'mb-3.5 border-b border-white/[0.08] pb-3 last:mb-0 last:border-b-0 last:pb-0';
const helpRow = 'mt-1 text-xs text-gray-300 first:mt-0';
const helpRowLabel = 'mr-1 font-semibold text-gray-400';
const helpSectionTitle = 'mb-1.5 mt-0 text-[13px] font-semibold text-white';

function HelpEntryBlock({ entry, primaryLabel }) {
  const title = primaryLabel || entry.iconName;
  const subtitle =
    primaryLabel && primaryLabel !== entry.iconName ? entry.iconName : null;
  return (
    <div className={helpPopoverSection}>
      <p className={helpSectionTitle}>{title}</p>
      {subtitle ? (
        <p className={helpRow} style={{ marginTop: '-2px', fontStyle: 'italic', color: '#9ca3af' }}>
          {subtitle}
        </p>
      ) : null}
      <p className={helpRow}>
        <span className={helpRowLabel}>Function:</span>
        {entry.function}
      </p>
      <p className={helpRow}>
        <span className={helpRowLabel}>When to use:</span>
        {entry.whenToUse}
      </p>
      <p className={helpRow}>
        <span className={helpRowLabel}>Help tip:</span>
        {entry.helpTip}
      </p>
      <p className={helpRow}>
        <span className={helpRowLabel}>Common mistake:</span>
        {entry.commonMistake}
      </p>
      <p className={helpRow}>
        <span className={helpRowLabel}>Clinical notes:</span>
        {entry.clinicalNotes}
      </p>
    </div>
  );
}

function SimpleToolCard({ title, description, hint, accent = 'Tool' }) {
  return (
    <motion.section
      initial={{ opacity: 0, filter: "blur(4px)", }}
      animate={{ opacity: 1, filter: "blur(0px)" }}
      exit={{ opacity: 0, filter: "blur(4px)" }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="pointer-events-auto absolute left-2.5 top-2.5 z-[1000] w-[min(200px,calc(100vw-24px))] max-w-[calc(100vw-24px)] max-h-[min(72vh,640px)] overflow-y-auto overflow-x-hidden rounded-md border border-slate-200/90 bg-white/[0.98] p-3 shadow-[0_16px_48px_rgba(15,23,42,0.12)] ring-1 ring-slate-900/[0.06] backdrop-blur-md [scrollbar-gutter:stable] motion-reduce:shadow-[0_8px_24px_rgba(15,23,42,0.08)] focus-within:ring-2 focus-within:ring-violet-500/25 focus-within:ring-offset-2 focus-within:ring-offset-white"
      aria-live="polite"
    >
      <p className={simpleEyebrow}>{accent}</p>
      <h4 className={simpleTitle}>{title}</h4>
      <p className={simpleBody}>{description}</p>
      {hint ? <p className={simpleHint}>{hint}</p> : null}
    </motion.section>
  );
}

const ToolRenderer = () => {
  const location = useLocation();
  const isFinalPreviewStep = isPreviewStepPath(location.pathname);
  const isFinalizationStep = isFinalizationPath(location.pathname);

  const currentStep = useWorkflowState((state) => state.currentStep);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpWrapRef = useRef(null);

  const [activeTool, toolSettings, undo, redo, resetScene, wireframeVisible, toggleWireframe] = useEditorStore(
    useShallow((state) => [
      state.activeTool,
      state.toolSettings,
      state.undo,
      state.redo,
      state.resetScene,
      state.wireframeVisible,
      state.toggleWireframe,
    ])
  );

  const patientData = useWorkflowState(
    useShallow((state) => state.patientData)
  );


  // Get amputation side and determine if we should swap Lateral/Mid labels
  const leg = patientData?.amputatedLeg || patientData?.keyMeasurements?.['Amputation Side'] || '';
  const shouldSwapLabels = String(leg).toLowerCase().includes('left');

  const stepTools = STEP_CONFIGS[currentStep]?.tools ?? [];
  const helpPanel = getToolHelpPanel(currentStep, activeTool, stepTools);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (helpWrapRef.current && !helpWrapRef.current.contains(e.target)) {
        setHelpOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setHelpOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [helpOpen]);

  // Render the appropriate tool based on activeTool
  const renderTool = () => {
    // Migrated steps render their tool cards via the shared registry
    // (each step's <ToolOverlay/>), so the legacy switch renders nothing for them.
    if (
      currentStep === SCAN_PROCESS_STEPS.AXES_ALIGNMENT ||
      currentStep === SCAN_PROCESS_STEPS.PREPARING_SCAN ||
      currentStep === MAIN_PROCESS_STEPS.RECTIFICATION ||
      currentStep === MAIN_PROCESS_STEPS.ELONGATION ||
      currentStep === MAIN_PROCESS_STEPS.TRIMLINE ||
      currentStep === MAIN_PROCESS_STEPS.ML_REDUCTION ||
      currentStep === MAIN_PROCESS_STEPS.FLARES ||
      currentStep === MAIN_PROCESS_STEPS.THICKNESS ||
      currentStep === MAIN_PROCESS_STEPS.VALVE ||
      currentStep === MAIN_PROCESS_STEPS.LOCKING_MECHANISM ||
      currentStep === MAIN_PROCESS_STEPS.LOGO_PLACEMENT ||
      currentStep === MAIN_PROCESS_STEPS.VALVE_PATTERN ||
      currentStep === MAIN_PROCESS_STEPS.PADS
    ) {
      return null;
    }

    switch (activeTool) {
      // Rectification tool cards migrated to the shared registry (guarded out
      // above); their scene handler lives in features/rectification.
      // Prepare Scan tool cards (scissors/eraser/pencil/rotate/measure/
      // circumference/lasso) migrated to the shared registry and are guarded out
      // above; their scene handler lives in features/prepare-scan.
      case 'ruler':
        return (
          <SimpleToolCard
            title="Ruler Tool"
            description="Measure point-to-point distances on model."
            hint="Compare both sides before confirming edits."
          />
        );
      case 'rotate':
        return (
          <SimpleToolCard
            title="Rotate Tool"
            description="Rotate model or selected parts for better view."
            hint="Use short rotations to keep orientation clear."
          />
        );
      case 'grid':
        return (
          <SimpleToolCard
            title="Grid Tool"
            description="Show alignment grid for precise positioning."
            hint="Use with Align for cleaner symmetry checks."
          />
        );
      case 'smooth':
        return (
          <SimpleToolCard
            title="Smooth Tool"
            description="Smooth local surface transitions on model."
            hint="Work in short passes to avoid over-smoothing."
          />
        );
      case 'extend':
        return (
          <SimpleToolCard
            title="Extend Tool"
            description="Extend model in selected direction."
            hint="Check surrounding contours after each extension."
          />
        );
      case 'preview':
        return (
          <SimpleToolCard
            accent="Review"
            title="Preview Tool"
            description="Preview pending changes before applying."
            hint="Confirm edge quality & fit-sensitive zones."
          />
        );
      case 'cut':
        return (
          <SimpleToolCard
            title="Cut Tool"
            description="Cut geometry along defined guide lines."
            hint="Validate path continuity before final cut."
          />
        );
      case 'line':
        return (
          <SimpleToolCard
            title="Line Tool"
            description="Draw reference lines directly on model."
            hint="Keep line segments smooth for cleaner downstream ops."
          />
        );
      case 'flare':
        return (
          <SimpleToolCard
            title="Flare Tool"
            description="Add flares to socket edge for comfort."
            hint="Balance flare depth with suspension needs."
          />
        );
      case 'adjust':
        return (
          <SimpleToolCard
            title="Adjust Tool"
            description="Apply targeted fine-tuning adjustments."
            hint="Use low-intensity edits for sensitive anatomy zones."
          />
        );
      case 'thickness':
        return (
          <SimpleToolCard
            title="Thickness Tool"
            description={`Set wall thickness: ${toolSettings.thickness?.value || 3.0} mm.`}
            hint="Review transitions near trimline after thickness changes."
          />
        );
      case 'lock':
        return (
          <SimpleToolCard
            title="Lock Tool"
            description="Add locking mechanism to socket design."
            hint="Verify clearance before placing lock component."
          />
        );
      case 'position':
        return (
          <SimpleToolCard
            title="Position Tool"
            description="Position components with precision controls."
            hint="Nudge in small increments for stable alignment."
          />
        );
      case 'align':
        return (
          <SimpleToolCard
            title="Align Tool"
            description="Align model elements on chosen axis."
            hint="Use grid view to confirm final alignment."
          />
        );
      case 'valve':
        return (
          <SimpleToolCard
            title="Valve Tool"
            description="Add valve component to socket."
            hint="Place where service access stays easy."
          />
        );
      case 'pad':
        return (
          <SimpleToolCard
            title="Pad Tool"
            description="Add comfort pads for pressure relief."
            hint="Spread pressure zones; avoid sharp pad edges."
          />
        );
      case 'review':
        return (
          <SimpleToolCard
            accent="Finalize"
            title="Review Tool"
            description="Review final design before export."
            hint="Check trimline, suspension, pressure zones, symmetry."
          />
        );
      case 'export':
        return (
          <SimpleToolCard
            accent="Output"
            title="Export Tool"
            description="Export final design package."
            hint="Confirm units, orientation, file format before export."
          />
        );
      case 'save':
        return (
          <SimpleToolCard
            accent="Session"
            title="Save Tool"
            description="Save current design progress."
            hint="Save after major edits to preserve safe restore points."
          />
        );
      // Elongation, Trimline, ML Reduction, Flares, Locking Mechanism, Logo
      // Placement, Valve Pattern and Pads tool cards migrated to their feature
      // steps (guarded out above); resolved via the shared registry / the step's
      // ToolOverlay extraTools.
      default:
        return null;
    }
  };

  const onDirectionButtonClick = (direction) => {
    changeCameraDirection(direction);
  };

  return (
    <>
      {/* Render the active tool */}
      <div className='ml-15 relative'>
        <AnimatePresence mode="wait">
          {activeTool !== 'none' && (
            <motion.div
              key={activeTool}
              initial={{ opacity: 0, filter: "blur(4px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, filter: "blur(4px)" }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              {renderTool()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>


      {/* Top right: Wireframe toggle + Viewport Gizmo */}
      {!isFinalPreviewStep && (
        <div className="pointer-events-auto absolute right-2.5 top-2.5 z-10 flex flex-col items-end gap-2">
          {/* <button
            type="button"
            className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/20 bg-white/[0.08] px-3 py-1.5 text-[13px] text-white backdrop-blur-sm transition-colors hover:border-white/35 hover:bg-white/15 aria-pressed:bg-blue-500/35 aria-pressed:border-blue-500/60"
            onClick={toggleWireframe}
            title={wireframeVisible ? 'Hide wireframe' : 'Show wireframe'}
            aria-pressed={wireframeVisible}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M2 12h4M18 12h4M4 6h16M4 18h16M6 2v4M6 18v4M18 2v4M18 18v4M12 2v4M12 18v4" />
            </svg>
            <span>Wireframe</span>
          </button> */}
          <div className="h-[120px] w-[120px] overflow-hidden rounded-lg bg-white/5 backdrop-blur-sm">
            <ViewportGizmo size={120} />
          </div>
        </div>
      )}

      {/* View Controls - Bottom Right of 3D Canvas */}
      {!isFinalizationStep && !isFinalPreviewStep && (
        <div className='absolute bottom-[10px] right-2.5 flex gap-1'>
          <button
            onClick={undo}
            title="Undo"
            className='flex items-center justify-center h-10 w-10 bg-[#fafafa]/50 border border-black/10 text-(--text-primary) hover:border-black/[0.14] hover:bg-[#f4f4f5] p-2 rounded-md'
          >
            <RotateCcw size={20} strokeWidth={2} aria-hidden />
          </button>

          <button
            onClick={redo}
            title="Redo"
            className='flex items-center justify-center h-10 w-10 bg-[#fafafa]/50 border border-black/10 text-(--text-primary) hover:border-black/[0.14] hover:bg-[#f4f4f5] p-2 rounded-md'
          >
            <RotateCw size={20} strokeWidth={2} aria-hidden />
          </button>

          <button
            title="Fullscreen"
            className='flex items-center justify-center h-10 w-10 bg-[#fafafa]/50 border border-black/10 text-(--text-primary) hover:border-black/[0.14] hover:bg-[#f4f4f5] p-2 rounded-md'
          >
            <Maximize2 size={20} strokeWidth={2} aria-hidden />
          </button>
        </div>
      )}

      {/* Question Button - Bottom of Left Toolbar */}
      {!isFinalizationStep && !isFinalPreviewStep && (
        <div ref={helpWrapRef} className="pointer-events-auto absolute bottom-2.5 left-2.5 z-10 flex flex-col items-start gap-2">
          <AnimatePresence>
            {helpOpen && (
              <motion.div
                key="help-popover"
                initial={{ opacity: 0, filter: "blur(4px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, filter: "blur(4px)" }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                id="tool-help-popover"
                className="relative z-50 -order-1 max-h-[min(72vh,520px)] w-[min(380px,calc(100vw-32px))] overflow-y-auto rounded-[10px] border border-white/12 bg-gray-900/97 p-3.5 px-4 text-[13px] leading-snug text-gray-100 shadow-2xl pointer-events-auto"
                role="dialog"
                aria-label="Tool help"
              >
                {helpPanel.mode === 'single' ? (
                  <HelpEntryBlock entry={helpPanel.entry} />
                ) : (
                  <>
                    <h4 className="mb-2.5 mt-0 text-sm font-bold text-amber-400">Step help</h4>
                    <p className="mb-3 mt-0 text-xs text-gray-400">
                      {activeTool !== 'none'
                        ? 'No detailed guide is defined for this tool yet. Below is an overview for this step.'
                        : 'Select a tool for focused tips, or review the references for this step.'}
                    </p>
                    {helpPanel.overview ? (
                      <HelpEntryBlock entry={helpPanel.overview} />
                    ) : null}
                    {helpPanel.extras?.length > 0 ? (
                      <>
                        <p className={helpSectionTitle} style={{ marginTop: 8 }}>
                          Canvas &amp; workflow
                        </p>
                        {helpPanel.extras.map((ex) => (
                          <HelpEntryBlock key={ex.id} entry={ex} />
                        ))}
                      </>
                    ) : null}
                    {helpPanel.toolEntries?.length > 0 ? (
                      <>
                        <p className={helpSectionTitle} style={{ marginTop: 8 }}>
                          Toolbar tools
                        </p>
                        {helpPanel.toolEntries.map((ex) => (
                          <HelpEntryBlock
                            key={ex.toolId}
                            entry={ex}
                            primaryLabel={TOOL_NAMES[ex.toolId] || ex.iconName}
                          />
                        ))}
                      </>
                    ) : null}
                    {!helpPanel.overview &&
                      (!helpPanel.extras || helpPanel.extras.length === 0) &&
                      (!helpPanel.toolEntries || helpPanel.toolEntries.length === 0) ? (
                      <p className={helpRow}>No help entries for this step yet.</p>
                    ) : null}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="button"
            title="Help"
            className="h-10 w-10 cursor-pointer rounded-full border-0 bg-amber-500 text-2xl font-bold text-white shadow-md transition-colors hover:bg-amber-600"
            aria-expanded={helpOpen}
            aria-controls="tool-help-popover"
            onClick={() => setHelpOpen((o) => !o)}
          >
            ?
          </motion.button>
        </div>
      )}


    </>
  );
};

export default ToolRenderer; 